import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api/api";
import ReportarProblema from "../components/ReportarProblema";

const font = "Montserrat, sans-serif";

const inputStyle = {
  padding: "10px 14px", borderRadius: 9, border: "1.5px solid rgba(0,0,0,0.12)",
  fontSize: 14, fontFamily: font, color: "#163244", background: "#fff",
  outline: "none", width: "100%", boxSizing: "border-box",
};
const labelStyle = {
  display: "block", fontSize: 10, fontWeight: 700, color: "#7a93a4",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5,
};

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

function useGeoDetect() {
  const [detecting, setDetecting] = useState(false);
  const [geoError,  setGeoError]  = useState("");

  const detect = (onResult) => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Tu navegador no soporta geolocalización."); return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "es" } }
          );
          const data = await res.json();
          const a = data.address || {};
          onResult({
            street:      [a.road, a.house_number].filter(Boolean).join(" ") || "",
            city:        a.city || a.town || a.village || a.municipality || "",
            province:    a.state || "",
            postal_code: a.postcode || "",
          });
        } catch {
          setGeoError("No se pudo obtener la dirección. Completala manualmente.");
        } finally { setDetecting(false); }
      },
      (err) => {
        setDetecting(false);
        if (err.code === 1) setGeoError("Permiso de ubicación denegado.");
        else setGeoError("No se pudo obtener la ubicación.");
      },
      { timeout: 8000, maximumAge: 60000 }
    );
  };

  return { detect, detecting, geoError, clearGeoError: () => setGeoError("") };
}

const passwordStrength = (pwd) => {
  let s = 0;
  if (pwd.length >= 8)   s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd))    s++;
  if (/[\W_]/.test(pwd)) s++;
  return s;
};
const STRENGTH_LABEL = ["", "Muy débil", "Débil", "Regular", "Buena", "Fuerte"];
const STRENGTH_COLOR = ["", "#c0392b", "#c0392b", "#b06a00", "#163244", "#1b7f4e"];

function compressImage(file, maxPx = 800, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width > height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Email Verification Widget ───────────────────────────────────────────────
function EmailVerifyWidget({ email, onVerified }) {
  const [step,     setStep]     = useState("idle");
  const [code,     setCode]     = useState("");
  const [error,    setError]    = useState("");
  const [resendCD, setResendCD] = useState(0);

  useEffect(() => {
    if (resendCD <= 0) return;
    const t = setTimeout(() => setResendCD(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCD]);

  const sendCode = async () => {
    setError(""); setStep("sending");
    try {
      await api.post("/auth/send-token", { email });
      setStep("code"); setResendCD(60);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo enviar el código.");
      setStep("idle");
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) { setError("Ingresá el código."); return; }
    setError(""); setStep("verifying");
    try {
      await api.post("/auth/verify-email-confirm", { token: code });
      setStep("done"); onVerified();
    } catch (err) {
      setError(err.response?.data?.error || "Código incorrecto o expirado.");
      setStep("code");
    }
  };

  if (step === "done") return null;

  return (
    <div style={{ marginTop: 10, background: "rgba(231,164,50,0.06)", borderRadius: 10, padding: "14px 14px", border: "1px solid rgba(231,164,50,0.25)" }}>
      {step === "idle" && (
        <>
          <div style={{ fontSize: 12, color: "#b06a00", fontWeight: 600, marginBottom: 10 }}>Verificá tu email para poder solicitar servicios.</div>
          <button onClick={sendCode} style={{ padding: "8px 18px", borderRadius: 50, border: "none", background: "#E7A432", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            Enviar código de verificación
          </button>
        </>
      )}
      {step === "sending" && (
        <div style={{ fontSize: 12, color: "#b06a00", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 13, height: 13, border: "2px solid #b06a00", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
          Enviando código...
        </div>
      )}
      {(step === "code" || step === "verifying") && (
        <>
          <div style={{ fontSize: 12, color: "#b06a00", marginBottom: 10 }}>
            Ingresá el código de 6 dígitos enviado a <strong>{email}</strong>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
              placeholder="000000" maxLength={6}
              style={{ ...inputStyle, width: 130, textAlign: "center", fontSize: 18, fontWeight: 700, letterSpacing: 6 }}
            />
            <button
              onClick={verifyCode}
              disabled={step === "verifying" || code.length < 6}
              style={{ padding: "8px 16px", borderRadius: 50, border: "none", background: code.length === 6 ? "#163244" : "#dce5ec", color: code.length === 6 ? "#fff" : "#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: code.length === 6 ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6 }}
            >
              {step === "verifying"
                ? <span style={{ width: 13, height: 13, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                : "Verificar"
              }
            </button>
          </div>
          {error && <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600, marginBottom: 6 }}>⚠ {error}</div>}
          <button onClick={sendCode} disabled={resendCD > 0} style={{ background: "none", border: "none", fontSize: 11, color: resendCD > 0 ? "#aab8c2" : "#163244", cursor: resendCD > 0 ? "default" : "pointer", fontFamily: font, fontWeight: 600, padding: 0 }}>
            {resendCD > 0 ? `Reenviar en ${resendCD}s` : "Reenviar código"}
          </button>
        </>
      )}
      {error && step === "idle" && <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600, marginTop: 6 }}>⚠ {error}</div>}
    </div>
  );
}

// ─── Phone Verification Widget ───────────────────────────────────────────────
function PhoneVerifyWidget({ phone, onVerified }) {
  const [step,     setStep]     = useState("idle");
  const [code,     setCode]     = useState("");
  const [error,    setError]    = useState("");
  const [resendCD, setResendCD] = useState(0);

  useEffect(() => {
    if (resendCD <= 0) return;
    const t = setTimeout(() => setResendCD(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCD]);

  const sendCode = async () => {
    setError(""); setStep("sending");
    try {
      await api.post("/phone/send");
      setStep("code"); setResendCD(60);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo enviar el código.");
      setStep("idle");
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) { setError("Ingresá el código."); return; }
    setError(""); setStep("verifying");
    try {
      await api.post("/phone/verify", { code });
      setStep("done"); onVerified();
    } catch (err) {
      setError(err.response?.data?.error || "Código incorrecto o expirado.");
      setStep("code");
    }
  };

  if (step === "done") return null;

  return (
    <div style={{ marginTop: 10, background: "rgba(16,185,129,0.05)", borderRadius: 10, padding: "14px 14px", border: "1px solid rgba(16,185,129,0.25)" }}>
      {step === "idle" && (
        <>
          <div style={{ fontSize: 12, color: "#1b7f4e", fontWeight: 600, marginBottom: 6 }}>
            Te vamos a enviar un código por SMS al número registrado.
          </div>
          {phone && <div style={{ fontSize: 11, color: "#7a93a4", marginBottom: 10 }}>Número: <strong style={{ color: "#163244" }}>{phone}</strong></div>}
          <button onClick={sendCode} style={{ padding: "8px 18px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 14 }}>📱</span> Enviar código por SMS
          </button>
        </>
      )}
      {step === "sending" && (
        <div style={{ fontSize: 12, color: "#1b7f4e", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 13, height: 13, border: "2px solid #1b7f4e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
          Enviando código por SMS...
        </div>
      )}
      {(step === "code" || step === "verifying") && (
        <>
          <div style={{ fontSize: 12, color: "#1b7f4e", marginBottom: 10 }}>
            Ingresá el código de 6 dígitos recibido por <strong>SMS</strong>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              value={code}
              onChange={e => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
              placeholder="000000" maxLength={6}
              style={{ ...inputStyle, width: 130, textAlign: "center", fontSize: 18, fontWeight: 700, letterSpacing: 6 }}
            />
            <button
              onClick={verifyCode}
              disabled={step === "verifying" || code.length < 6}
              style={{ padding: "8px 16px", borderRadius: 50, border: "none", background: code.length === 6 ? "#1b7f4e" : "#dce5ec", color: code.length === 6 ? "#fff" : "#7a93a4", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: code.length === 6 ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6 }}
            >
              {step === "verifying"
                ? <span style={{ width: 13, height: 13, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                : "Verificar"
              }
            </button>
          </div>
          {error && <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600, marginBottom: 6 }}>⚠ {error}</div>}
          <button onClick={sendCode} disabled={resendCD > 0} style={{ background: "none", border: "none", fontSize: 11, color: resendCD > 0 ? "#aab8c2" : "#163244", cursor: resendCD > 0 ? "default" : "pointer", fontFamily: font, fontWeight: 600, padding: 0 }}>
            {resendCD > 0 ? `Reenviar en ${resendCD}s` : "Reenviar código"}
          </button>
        </>
      )}
      {error && step === "idle" && <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600, marginTop: 6 }}>⚠ {error}</div>}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
function Sidebar({ profileData, verification, setVerification, user, photo, onPhotoChange }) {
  const inputRef              = useRef(null);
  const [compressing, setCompressing] = useState(false);
  const [photoError,  setPhotoError]  = useState("");
  const [photoSaving, setPhotoSaving] = useState(false);
  const [photoSaved,  setPhotoSaved]  = useState(false);

  const initials = [profileData.first_name, profileData.last_name]
    .filter(Boolean).map(n => n[0]).join("").toUpperCase() || user?.name?.[0]?.toUpperCase() || "?";
  const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(" ") || user?.name || "";

  const processPhoto = async (file) => {
    setPhotoError(""); setPhotoSaved(false);
    if (!file) return;
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) {
      setPhotoError("Usá JPG, PNG o WebP."); return;
    }
    if (file.size > 10 * 1024 * 1024) { setPhotoError("Máx. 10 MB."); return; }
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setPhotoSaving(true);
      await api.post("/users/me/photo", { photo: compressed });
      onPhotoChange(compressed);
      setPhotoSaved(true);
      setTimeout(() => setPhotoSaved(false), 2500);
    } catch {
      setPhotoError("No se pudo guardar la foto.");
    } finally { setCompressing(false); setPhotoSaving(false); }
  };

  const sCard = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 14, fontFamily: font };
  const sHdr  = { padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 };
  const sAcc  = { width: 3, height: 13, background: "#E7A432", borderRadius: 2, flexShrink: 0 };
  const sTit  = { fontSize: 11, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: 1 };

  return (
    <div style={{ fontFamily: font }}>

      {/* Card usuario */}
      <div style={{ background: "#163244", borderRadius: 14, padding: "22px 18px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 85% 15%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div style={{ position: "relative" }}>
            {photo ? (
              <img src={photo} alt="Foto" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "3px solid #E7A432" }} />
            ) : (
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#163244" }}>
                {initials}
              </div>
            )}
            <button onClick={() => inputRef.current?.click()} disabled={compressing || photoSaving}
              style={{ position: "absolute", bottom: 0, right: 0, width: 26, height: 26, borderRadius: "50%", background: "#fff", border: "2px solid #163244", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>
              {compressing || photoSaving ? "⏳" : "📷"}
            </button>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={e => processPhoto(e.target.files?.[0])} />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, marginBottom: 2 }}>{fullName || "Tu perfil"}</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{user?.email}</div>
            {user?.role === "admin" && <div style={{ color: "#E7A432", fontSize: 10, fontWeight: 700, marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>Administrador</div>}
          </div>
          {photoSaved && <div style={{ fontSize: 11, color: "#1b7f4e", fontWeight: 700, background: "rgba(27,127,78,0.15)", borderRadius: 20, padding: "2px 10px" }}>✓ Foto guardada</div>}
          {photoError && <div style={{ fontSize: 11, color: "#c0392b", fontWeight: 600 }}>⚠ {photoError}</div>}
        </div>
      </div>

      {/* Verificaciones */}
      <div style={sCard}>
        <div style={sHdr}><div style={sAcc}/><span style={sTit}>Verificaciones</span></div>
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>

          {/* Email */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 9, background: verification.email_verified ? "rgba(27,127,78,0.07)" : "rgba(176,106,0,0.07)", border: `1px solid ${verification.email_verified ? "rgba(27,127,78,0.2)" : "rgba(176,106,0,0.2)"}` }}>
            <span style={{ fontSize: 16 }}>✉️</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: verification.email_verified ? "#1b7f4e" : "#b06a00", flex: 1 }}>Email</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: verification.email_verified ? "#1b7f4e" : "#b06a00" }}>
              {verification.email_verified ? "✓ Verificado" : "Pendiente"}
            </span>
          </div>
          {!verification.email_verified && user?.email && (
            <EmailVerifyWidget
              email={user.email}
              onVerified={() => setVerification(v => ({ ...v, email_verified: true }))}
            />
          )}

          {/* Teléfono */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 9, background: verification.phone_verified ? "rgba(27,127,78,0.07)" : "rgba(176,106,0,0.07)", border: `1px solid ${verification.phone_verified ? "rgba(27,127,78,0.2)" : "rgba(176,106,0,0.2)"}` }}>
            <span style={{ fontSize: 16 }}>📱</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: verification.phone_verified ? "#1b7f4e" : "#b06a00", flex: 1 }}>Teléfono</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: verification.phone_verified ? "#1b7f4e" : "#b06a00" }}>
              {verification.phone_verified ? "✓ Verificado" : "Pendiente"}
            </span>
          </div>
          {!verification.phone_verified && (
            <PhoneVerifyWidget
              phone={profileData.phone}
              onVerified={() => setVerification(v => ({ ...v, phone_verified: true }))}
            />
          )}

        </div>
      </div>

      {/* Info cuenta */}
      <div style={sCard}>
        <div style={sHdr}><div style={sAcc}/><span style={sTit}>Mi cuenta</span></div>
        <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            { label: "Rol",     value: user?.role === "admin" ? "Administrador" : user?.role === "prestador" ? "Prestador" : "Cliente" },
            { label: "Usuario", value: profileData.username || "—" },
            { label: "DNI",     value: profileData.dni || "—" },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid rgba(0,0,0,0.05)", fontSize: 12 }}>
              <span style={{ color: "#7a93a4", fontWeight: 500 }}>{label}</span>
              <span style={{ color: "#163244", fontWeight: 700 }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tip */}
      <div style={sCard}>
        <div style={{ padding: "14px 16px" }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(231,164,50,0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#E7A432" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#163244", marginBottom: 3 }}>Mantené tu perfil actualizado</div>
          <div style={{ fontSize: 11, color: "#7a93a4", lineHeight: 1.55 }}>Un perfil completo acelera el proceso de solicitudes y mejora tu experiencia en Fixo.</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, logout } = useContext(AuthContext);
  const navigate         = useNavigate();

  const [tab,             setTab]             = useState("personal");
  const [profileFetching, setProfileFetching] = useState(true);
  const [profileLoading,  setProfileLoading]  = useState(false);
  const [fieldErrors,     setFieldErrors]     = useState({});
  const [toast,           setToast]           = useState(null);
  const [photo,           setPhoto]           = useState("");

  const [profileData, setProfileData] = useState({
    first_name: "", last_name: "", username: "", phone: "",
    dni: "", birthdate: "", street: "", city: "", province: "", postal_code: "",
  });
  const [verification, setVerification] = useState({ email_verified: false, phone_verified: false });

  const [passData,    setPassData]    = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passLoading, setPassLoading] = useState(false);
  const [passError,   setPassError]   = useState("");
  const [showPass,    setShowPass]    = useState({ current: false, new: false, confirm: false });

  const { detect, detecting, geoError, clearGeoError } = useGeoDetect();

  useEffect(() => {
    api.get("/users/me")
      .then(res => {
        const d = res.data.user || res.data;
        setProfileData({
          first_name:  d.first_name  || "",
          last_name:   d.last_name   || "",
          username:    d.username    || "",
          phone:       d.phone       || "",
          dni:         d.dni         || "",
          birthdate:   d.birthdate ? d.birthdate.split("T")[0] : "",
          street:      d.street      || "",
          city:        d.city        || "",
          province:    d.province    || "",
          postal_code: d.postal_code || "",
        });
        setVerification({ email_verified: !!d.email_verified, phone_verified: !!d.phone_verified });
        setPhoto(d.photo_url || "");
      })
      .catch(() => { if (user) setProfileData(p => ({ ...p, first_name: user.name || "" })); })
      .finally(() => setProfileFetching(false));
  }, []);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const handleProfileSave = async () => {
    setFieldErrors({});
    if (!profileData.first_name.trim()) { showToast("El nombre es obligatorio.", "error"); return; }
    setProfileLoading(true);
    try {
      await api.put("/users/me", profileData);
      showToast("Datos actualizados correctamente.");
    } catch (err) {
      const msg = err.response?.data?.error || "Error al actualizar los datos.";
      if (err.response?.status === 409 && msg.toLowerCase().includes("usuario")) {
        setFieldErrors({ username: "Este nombre de usuario ya está en uso." });
      } else {
        showToast(msg, "error");
      }
    } finally { setProfileLoading(false); }
  };

  const handlePasswordChange = async () => {
    setPassError("");
    const { currentPassword, newPassword, confirmPassword } = passData;
    if (!currentPassword || !newPassword || !confirmPassword) { setPassError("Completá todos los campos."); return; }
    if (newPassword !== confirmPassword) { setPassError("Las contraseñas no coinciden."); return; }
    if (!passwordRegex.test(newPassword)) { setPassError("Mínimo 8 caracteres con mayúsculas, minúsculas, números y símbolos."); return; }
    setPassLoading(true);
    try {
      await api.put("/users/change-password", { currentPassword, newPassword });
      setPassData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      showToast("Contraseña actualizada. Cerrando sesión...");
      setTimeout(() => { logout(); navigate("/"); }, 2000);
    } catch (err) {
      setPassError(err.response?.data?.error || "Error al cambiar la contraseña.");
    } finally { setPassLoading(false); }
  };

  const strength = passwordStrength(passData.newPassword);

  const TABS = [
    { id: "personal", label: "Datos personales", icon: "👤" },
    { id: "address",  label: "Domicilio",         icon: "📍" },
    { id: "password", label: "Contraseña",         icon: "🔒" },
  ];

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "28px 24px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20, alignItems: "start" }}>

            {/* Columna izquierda */}
            <div>
              <div style={{ marginBottom: 18 }}>
                <h1 style={{ margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: "#163244" }}>Mi perfil</h1>
                <p style={{ margin: 0, color: "#7a93a4", fontSize: 13 }}>Administrá tu información personal y configuración de cuenta.</p>
              </div>

              {/* Banner verificación pendiente */}
              {(!verification.email_verified || !verification.phone_verified) && (
                <div style={{ background: "rgba(176,106,0,0.07)", border: "1.5px solid rgba(176,106,0,0.25)", borderRadius: 12, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>⚠️</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#163244" }}>
                      {!verification.email_verified && !verification.phone_verified
                        ? "Verificá tu email y teléfono"
                        : !verification.email_verified
                          ? "Verificá tu email"
                          : "Verificá tu teléfono"
                      }
                    </div>
                    <div style={{ fontSize: 12, color: "#7a93a4", marginTop: 2 }}>
                      Necesitás verificar tus datos de contacto para poder solicitar servicios. Usá los botones en la sección de verificaciones.
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div style={{ display: "flex", gap: 5, marginBottom: 18, background: "#fff", borderRadius: 12, padding: 5, border: "1px solid rgba(0,0,0,0.07)" }}>
                {TABS.map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} style={{
                    flex: 1, padding: "9px 6px", borderRadius: 9, border: "none",
                    background: tab === t.id ? "#163244" : "transparent",
                    color: tab === t.id ? "#fff" : "#5a6e7f",
                    fontFamily: font, fontWeight: tab === t.id ? 700 : 500,
                    fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 6, transition: "all 0.15s",
                  }}>
                    <span>{t.icon}</span><span>{t.label}</span>
                  </button>
                ))}
              </div>

              {profileFetching ? (
                <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
                  <span style={{ display: "inline-block", width: 26, height: 26, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 12 }} /><br/>
                  Cargando perfil...
                </div>
              ) : (
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", padding: "24px" }}>

                  {/* Tab: Datos personales */}
                  {tab === "personal" && (
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#163244", marginBottom: 18, paddingBottom: 10, borderBottom: "1.5px solid rgba(0,0,0,0.06)" }}>Datos personales</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                        <div>
                          <label style={labelStyle}>Nombre</label>
                          <input style={inputStyle} value={profileData.first_name} onChange={e => setProfileData(p => ({ ...p, first_name: e.target.value }))} placeholder="Juan" />
                        </div>
                        <div>
                          <label style={labelStyle}>Apellido</label>
                          <input style={inputStyle} value={profileData.last_name} onChange={e => setProfileData(p => ({ ...p, last_name: e.target.value }))} placeholder="Pérez" />
                        </div>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Nombre de usuario</label>
                        <input
                          style={{ ...inputStyle, borderColor: fieldErrors.username ? "#c0392b" : "rgba(0,0,0,0.12)" }}
                          value={profileData.username}
                          onChange={e => { setProfileData(p => ({ ...p, username: e.target.value })); if (fieldErrors.username) setFieldErrors(p => ({ ...p, username: "" })); }}
                          placeholder="3-20 caracteres, letras y números"
                        />
                        {fieldErrors.username && <div style={{ fontSize: 11, color: "#c0392b", marginTop: 4, fontWeight: 600 }}>{fieldErrors.username}</div>}
                        {!fieldErrors.username && <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 4 }}>Podés usarlo para iniciar sesión en lugar del email.</div>}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                        <div>
                          <label style={labelStyle}>DNI / CUIL</label>
                          <input style={inputStyle} value={profileData.dni} onChange={e => setProfileData(p => ({ ...p, dni: e.target.value }))} />
                        </div>
                        <div>
                          <label style={labelStyle}>Fecha de nacimiento</label>
                          <input type="date" style={inputStyle} value={profileData.birthdate} onChange={e => setProfileData(p => ({ ...p, birthdate: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Teléfono</label>
                        <input style={inputStyle} value={profileData.phone} onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))} placeholder="Ej: 11 5555-1234" />
                        <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 4 }}>Código de área sin 0 + número. Al cambiar el teléfono deberás verificarlo nuevamente.</div>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <label style={labelStyle}>Email</label>
                        <input style={{ ...inputStyle, background: "#f5f7fa", color: "#aab8c2", cursor: "not-allowed" }} value={user?.email || ""} disabled />
                        <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 4 }}>El email no puede modificarse desde aquí.</div>
                      </div>
                      <button onClick={handleProfileSave} disabled={profileLoading} style={{ padding: "11px 28px", borderRadius: 50, border: "none", background: "#E7A432", color: "#163244", fontFamily: font, fontWeight: 800, fontSize: 13, cursor: profileLoading ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {profileLoading
                          ? <><span style={{ width: 14, height: 14, border: "2px solid #163244", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Guardando…</>
                          : "Guardar cambios"}
                      </button>
                    </div>
                  )}

                  {/* Tab: Domicilio — edición deshabilitada en web (DEUDA-WEB-1.1) */}
                  {tab === "address" && (
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#163244", marginBottom: 18, paddingBottom: 10, borderBottom: "1.5px solid rgba(0,0,0,0.06)" }}>Domicilio</div>

                      {/* Banner: edición solo desde mobile */}
                      <div style={{ marginBottom: 18, background: "rgba(231,164,50,0.06)", borderRadius: 10, padding: "14px 16px", border: "1px solid rgba(231,164,50,0.25)" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#b06a00", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                          📱 Editá tu domicilio desde la app de FIXO
                        </div>
                        <div style={{ fontSize: 12, color: "#7a93a4", lineHeight: 1.5 }}>
                          Esta sección quedó disponible solo desde la aplicación móvil.
                        </div>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <button disabled
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.12)", background: "#f5f7fa", color: "#aab8c2", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "not-allowed" }}>
                          📍 Detectar mi ubicación automáticamente
                        </button>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>Calle y número</label>
                        <input style={{ ...inputStyle, background: "#f5f7fa", color: "#aab8c2", cursor: "not-allowed" }} value={profileData.street} disabled placeholder="Av. Corrientes 1234, Piso 3 A" />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                        <div>
                          <label style={labelStyle}>Localidad / Ciudad</label>
                          <input style={{ ...inputStyle, background: "#f5f7fa", color: "#aab8c2", cursor: "not-allowed" }} value={profileData.city} disabled />
                        </div>
                        <div>
                          <label style={labelStyle}>Provincia</label>
                          <input style={{ ...inputStyle, background: "#f5f7fa", color: "#aab8c2", cursor: "not-allowed" }} value={profileData.province} disabled />
                        </div>
                      </div>
                      <div style={{ marginBottom: 20, width: "50%" }}>
                        <label style={labelStyle}>Código postal</label>
                        <input style={{ ...inputStyle, background: "#f5f7fa", color: "#aab8c2", cursor: "not-allowed" }} value={profileData.postal_code} disabled />
                      </div>
                      <button disabled style={{ padding: "11px 28px", borderRadius: 50, border: "none", background: "#f5f7fa", color: "#aab8c2", fontFamily: font, fontWeight: 800, fontSize: 13, cursor: "not-allowed", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        Guardar domicilio
                      </button>
                    </div>
                  )}

                  {/* Tab: Contraseña */}
                  {tab === "password" && (
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#163244", marginBottom: 18, paddingBottom: 10, borderBottom: "1.5px solid rgba(0,0,0,0.06)" }}>Cambiar contraseña</div>
                      {passError && (
                        <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#c0392b", fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                          ⚠ {passError}
                          <button onClick={() => setPassError("")} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 16 }}>✕</button>
                        </div>
                      )}
                      {[
                        { key: "currentPassword", label: "Contraseña actual",          showKey: "current" },
                        { key: "newPassword",      label: "Nueva contraseña",           showKey: "new"     },
                        { key: "confirmPassword",  label: "Confirmar nueva contraseña", showKey: "confirm" },
                      ].map(({ key, label, showKey }) => (
                        <div key={key} style={{ marginBottom: 14, position: "relative" }}>
                          <label style={labelStyle}>{label}</label>
                          <input type={showPass[showKey] ? "text" : "password"} style={{ ...inputStyle, paddingRight: 42 }} value={passData[key]} onChange={e => setPassData(p => ({ ...p, [key]: e.target.value }))} />
                          <button onClick={() => setShowPass(p => ({ ...p, [showKey]: !p[showKey] }))} style={{ position: "absolute", right: 12, bottom: 10, background: "none", border: "none", cursor: "pointer", color: "#7a93a4", fontSize: 15 }}>
                            {showPass[showKey] ? "🙈" : "👁"}
                          </button>
                        </div>
                      ))}
                      {passData.newPassword && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ height: 4, borderRadius: 2, background: "#e8eef2", overflow: "hidden", marginBottom: 4 }}>
                            <div style={{ height: "100%", borderRadius: 2, background: STRENGTH_COLOR[strength], width: `${(strength / 5) * 100}%`, transition: "width 0.3s" }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: STRENGTH_COLOR[strength] }}>{STRENGTH_LABEL[strength]}</span>
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: "#7a93a4", marginBottom: 20, lineHeight: 1.55 }}>
                        Mínimo 8 caracteres con mayúsculas, minúsculas, números y símbolos. Al cambiar la contraseña se cerrará la sesión.
                      </div>
                      <button onClick={handlePasswordChange} disabled={passLoading} style={{ padding: "11px 28px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 800, fontSize: 13, cursor: passLoading ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {passLoading
                          ? <><span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Cambiando…</>
                          : "Cambiar contraseña"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            {!profileFetching && (
              <Sidebar
                profileData={profileData}
                verification={verification}
                setVerification={setVerification}
                user={user}
                photo={photo}
                onPhotoChange={setPhoto}
              />
            )}
          </div>
          {/* Guías de la Comunidad, punto 8: el camino para avisar, a la vista. */}
          <ReportarProblema />
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.type === "error" ? "#c0392b" : "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: `4px solid ${toast.type === "error" ? "#e57373" : "#E7A432"}`, whiteSpace: "nowrap", fontFamily: font }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        input:focus { border-color: #163244 !important; outline: none; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
